import { useEffect, useState } from 'react'
import { lumi } from '../lib/lumi'
import {RefreshCw, Clock, CheckCircle, XCircle, AlertCircle} from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'

interface SyncRecord {
  _id: string
  syncedAt: string
  source: string
  status: string
  couponsAdded: number
  couponsUpdated: number
  errorMessage?: string
}

export default function AdminAffiliateSyncs() {
  const { user } = useAuth()
  const [syncs, setSyncs] = useState<SyncRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [selectedSource, setSelectedSource] = useState('mock')

  useEffect(() => {
    loadSyncs()
  }, [])

  const loadSyncs = async () => {
    try {
      setLoading(true)
      const result = await lumi.entities.affiliateFeedSyncs.list({
        sort: { syncedAt: -1 },
        limit: 50
      })
      setSyncs(result.list || [])
    } catch (error) {
      console.error('Error loading syncs:', error)
      toast.error('Failed to load sync history')
    } finally {
      setLoading(false)
    }
  }

  const triggerSync = async () => {
    if (!user) {
      toast.error('Please sign in to trigger syncs')
      return
    }

    try {
      setSyncing(true)
      toast.loading('Syncing affiliate feeds...', { id: 'sync' })
      
      const response = await lumi.functions.invoke('sync-affiliate-feeds', {
        method: 'POST',
        body: {
          source: selectedSource,
          forceSync: true
        }
      })

      if (response.success) {
        toast.success(
          `Synced ${response.couponsAdded + response.couponsUpdated} coupons!`,
          { id: 'sync' }
        )
        loadSyncs()
      } else {
        toast.error('Sync failed', { id: 'sync' })
      }
    } catch (error: any) {
      console.error('Error triggering sync:', error)
      toast.error(error?.data?.error || 'Failed to trigger sync', { id: 'sync' })
    } finally {
      setSyncing(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />
      default:
        return <Clock className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusBadge = (status: string) => {
    const classes = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800'
    }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading sync history...</div>
      </div>
    )
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Affiliate Feed Syncs</h1>
            <p className="text-gray-600 mb-6">
              Manage automatic coupon synchronization from affiliate networks. Currently using mock data - add API keys to connect real networks.
            </p>
            
            {/* Trigger Sync Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Manual Sync</h2>
              <div className="flex items-center gap-4">
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={syncing}
                >
                  <option value="mock">Mock Data (Testing)</option>
                  <option value="all">All Networks (Configured)</option>
                  <optgroup label="Core Networks">
                    <option value="cj">Commission Junction (CJ)</option>
                    <option value="rakuten">Rakuten Advertising</option>
                    <option value="shareasale">ShareASale</option>
                    <option value="impact">Impact.com</option>
                    <option value="awin">Awin</option>
                  </optgroup>
                  <optgroup label="Extended Networks">
                    <option value="pepperjam">Pepperjam (Ascend)</option>
                    <option value="flexoffers">FlexOffers</option>
                    <option value="clickbank">ClickBank</option>
                    <option value="partnerize">Partnerize</option>
                    <option value="admitad">Admitad</option>
                    <option value="tradetracker">TradeTracker</option>
                  </optgroup>
                  <optgroup label="Major Platforms">
                    <option value="amazon">Amazon Associates</option>
                    <option value="ebay">eBay Partner Network</option>
                  </optgroup>
                  <optgroup label="Monetization & Aggregators">
                    <option value="skimlinks">Skimlinks</option>
                    <option value="viglink">VigLink (Sovrn)</option>
                    <option value="couponapi">CouponAPI</option>
                  </optgroup>
                  <optgroup label="SaaS & Tracking">
                    <option value="refersion">Refersion</option>
                    <option value="linkconnector">LinkConnector</option>
                    <option value="webgains">Webgains</option>
                  </optgroup>
                </select>
                
                <button
                  onClick={triggerSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Trigger Sync'}
                </button>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Automated Schedule:</strong> Syncs run automatically every 6 hours. Manual sync forces an immediate update.
                </p>
              </div>
            </div>
          </div>

          {/* Sync History */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Sync History</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {syncs.map((sync) => (
                    <tr key={sync._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(sync.status)}
                          {getStatusBadge(sync.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{sync.source}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {new Date(sync.syncedAt).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-green-600 font-semibold">
                          +{sync.couponsAdded}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-blue-600 font-semibold">
                          ↻{sync.couponsUpdated}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {sync.errorMessage ? (
                          <span className="text-sm text-red-600">{sync.errorMessage}</span>
                        ) : (
                          <span className="text-sm text-gray-500">
                            Total: {sync.couponsAdded + sync.couponsUpdated} coupons
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {syncs.length === 0 && (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No sync history yet</p>
                <p className="text-gray-400 text-sm">Trigger your first sync to get started</p>
              </div>
            )}
          </div>

          {/* API Key Setup Guide */}
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Setup Affiliate Networks</h2>
            <div className="space-y-4 text-sm text-gray-600">
              <p>To connect affiliate networks, add required credentials in the Lumi environment variables:</p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Core Networks</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>CJ_API_KEY</strong> - Commission Junction</li>
                    <li><strong>RAKUTEN_API_KEY</strong> - Rakuten Advertising</li>
                    <li><strong>SHAREASALE_API_TOKEN</strong>, SHAREASALE_AFFILIATE_ID, SHAREASALE_API_SECRET</li>
                    <li><strong>IMPACT_ACCOUNT_SID</strong>, IMPACT_AUTH_TOKEN</li>
                    <li><strong>AWIN_API_KEY</strong>, AWIN_PUBLISHER_ID</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extended Networks</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>PEPPERJAM_API_KEY</strong> - Pepperjam/Ascend</li>
                    <li><strong>FLEXOFFERS_API_KEY</strong> - FlexOffers</li>
                    <li><strong>CLICKBANK_API_KEY</strong>, CLICKBANK_ACCOUNT_NAME</li>
                    <li><strong>PARTNERIZE_API_KEY</strong>, PARTNERIZE_PUBLISHER_ID</li>
                    <li><strong>ADMITAD_CLIENT_ID</strong>, ADMITAD_CLIENT_SECRET</li>
                    <li><strong>TRADETRACKER_CUSTOMER_ID</strong>, TRADETRACKER_PASSPHRASE</li>
                  </ul>
                </div>
              </div>
              
              <p className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                <strong>💡 Tip:</strong> Select "All Networks" to sync from all configured networks simultaneously. Each network requires specific API credentials - see your affiliate network dashboard for details.
              </p>
              
              <p className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                <strong>Note:</strong> Without API keys, the system uses mock data for testing. Add keys to pull live coupon feeds from real affiliate networks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}